import { create } from '@bufbuild/protobuf'
import { createConnectQueryKey, useMutation, useQuery } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { useStore } from '@tanstack/react-store'
import { createFileRoute, Link } from '@tanstack/react-router'
import { type FormEvent, type ReactNode, useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  Lock,
  Loader2,
  Mail,
  Plus,
  RefreshCw,
  Rocket,
  Save,
  Send,
  Sparkles,
  Undo2,
  Users,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  createEmailMarketingCampaign,
  getEmailMarketingOverview,
  launchEmailMarketingCampaign,
  listEmailMarketingCampaigns,
  listEmailMarketingResources,
  refreshEmailMarketingCampaignStatus,
  getSystemSettings,
  sendEmailMarketingCampaignTest,
  updateEmailMarketingCampaign,
} from '../../gen/cortado/v1/admin-AdminService_connectquery'
import {
  CreateEmailMarketingCampaignRequestSchema,
  GetEmailMarketingOverviewRequestSchema,
  GetSystemSettingsAdminRequestSchema,
  LaunchEmailMarketingCampaignRequestSchema,
  ListEmailMarketingCampaignsRequestSchema,
  ListEmailMarketingResourcesRequestSchema,
  RefreshEmailMarketingCampaignStatusRequestSchema,
  SendEmailMarketingCampaignTestRequestSchema,
  UpdateEmailMarketingCampaignRequestSchema,
  type EmailMarketingCampaign,
  type EmailMarketingResource,
} from '../../gen/cortado/v1/admin_pb'
import {
  EmailMarketingAudienceEnum,
  EmailMarketingCampaignStatusEnum,
  EmailMarketingResourceTypeEnum,
} from '../../gen/cortado/v1/settings_pb'
import {
  EMAIL_MARKETING_AUDIENCES,
  audienceDescription,
  audienceLabel,
  audienceMemberCount,
  audienceSegmentsReady,
  isAudienceSelected,
} from '../../lib/email-marketing-audiences'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select'
import { Textarea } from '../../components/ui/textarea'
import { cn } from '../../lib/utils'
import { authStore } from '../../stores/auth.store'
import {
  type CampaignForm,
  type LaunchStep,
  campaignTopicStatus,
  defaultForm,
  formFromCampaign,
  formatTimestamp,
  htmlToPlainText,
  isCampaignEditable,
  isFormDirty,
  lastCampaignActivity,
  launchChecklist,
  missingFormFields,
  parseRecipients,
  statusLabel,
  statusVariant,
} from './-email-marketing/campaign-state'
import { DeliverySheet } from './-email-marketing/delivery-sheet'
import { CampaignHtmlEditor } from './-email-marketing/html-editor'
import {
  UNSUBSCRIBE_URL_PLACEHOLDER,
  normalizePostalAddress,
  withUnsubscribeFooterPreview,
} from './-email-marketing/unsubscribe-footer'

export const Route = createFileRoute('/_admin/email-marketing')({
  component: EmailMarketingPage,
})

const campaignListKey = createConnectQueryKey({
  schema: listEmailMarketingCampaigns,
  cardinality: undefined,
})

const overviewKey = createConnectQueryKey({
  schema: getEmailMarketingOverview,
  cardinality: undefined,
})

const resourceListKey = createConnectQueryKey({
  schema: listEmailMarketingResources,
  cardinality: undefined,
})

function EmailMarketingPage() {
  const queryClient = useQueryClient()
  const adminEmail = useStore(authStore, (state) => state.user?.email ?? '')
  const [selectedCampaignId, setSelectedCampaignId] = useState('')
  const [creatingNew, setCreatingNew] = useState(false)
  const [form, setForm] = useState<CampaignForm>(() => defaultForm())
  const [testRecipients, setTestRecipients] = useState('')
  const [launchDialogOpen, setLaunchDialogOpen] = useState(false)
  const [deliveryOpen, setDeliveryOpen] = useState(false)

  const overviewRequest = useMemo(() => create(GetEmailMarketingOverviewRequestSchema, {}), [])
  const campaignsRequest = useMemo(
    () => create(ListEmailMarketingCampaignsRequestSchema, { limit: 100 }),
    [],
  )
  const segmentsRequest = useMemo(
    () =>
      create(ListEmailMarketingResourcesRequestSchema, {
        type: EmailMarketingResourceTypeEnum.SEGMENT,
      }),
    [],
  )
  const topicsRequest = useMemo(
    () =>
      create(ListEmailMarketingResourcesRequestSchema, {
        type: EmailMarketingResourceTypeEnum.TOPIC,
      }),
    [],
  )

  const settingsRequest = useMemo(() => create(GetSystemSettingsAdminRequestSchema, {}), [])
  const settingsQuery = useQuery(getSystemSettings, settingsRequest)
  const postalAddress = normalizePostalAddress(settingsQuery.data?.settings?.postalAddress ?? '')
  const postalAddressReady = postalAddress.length > 0

  const overviewQuery = useQuery(getEmailMarketingOverview, overviewRequest)
  const campaignsQuery = useQuery(listEmailMarketingCampaigns, campaignsRequest)
  const refetchOverview = overviewQuery.refetch
  const refetchCampaigns = campaignsQuery.refetch

  const overview = overviewQuery.data?.overview
  const resendReady = overview?.resendConfigured ?? false
  const segmentsQuery = useQuery(listEmailMarketingResources, segmentsRequest, {
    enabled: resendReady,
  })
  const topicsQuery = useQuery(listEmailMarketingResources, topicsRequest, {
    enabled: resendReady,
  })
  const segments = segmentsQuery.data?.resources ?? []
  const topics = topicsQuery.data?.resources ?? []
  const defaultTopicId = topics[0]?.id ?? ''
  const campaigns = campaignsQuery.data?.campaigns ?? []
  const selectedCampaign = creatingNew
    ? undefined
    : selectedCampaignId
      ? campaigns.find((campaign) => campaign.id === selectedCampaignId)
      : campaigns[0]
  const selectedCampaignPending = Boolean(
    !creatingNew && selectedCampaignId && !selectedCampaign,
  )
  const formDirty = isFormDirty(form, selectedCampaign)
  const canEdit = !selectedCampaignPending && isCampaignEditable(selectedCampaign)
  const syncRunning = overview?.syncStatus === 'running'
  const campaignLaunching = campaigns.some(
    (campaign) => campaign.status === EmailMarketingCampaignStatusEnum.LAUNCHING,
  )
  const selectedCampaignTopic = campaignTopicStatus(selectedCampaign, topics)
  const selectedCampaignTopicValid = Boolean(
    selectedCampaign?.resendTopicId && !selectedCampaignTopic.missing,
  )
  const formTopicExists = topics.some((topic) => topic.id === form.resendTopicId)
  const allResourcesReady = Boolean(
    audienceSegmentsReady(overview) &&
      overview?.productUpdatesTopic?.configured &&
      overview.announcementsTopic?.configured,
  )
  const deliveryReady = resendReady && allResourcesReady

  useEffect(() => {
    if (creatingNew || selectedCampaignId || campaigns.length === 0) return
    setSelectedCampaignId(campaigns[0].id)
  }, [campaigns, creatingNew, selectedCampaignId])

  useEffect(() => {
    if (creatingNew) return
    if (selectedCampaign) {
      setForm(formFromCampaign(selectedCampaign))
      return
    }
    if (!selectedCampaignId) setForm(defaultForm(defaultTopicId))
  }, [creatingNew, defaultTopicId, selectedCampaign, selectedCampaignId])

  useEffect(() => {
    if (!creatingNew || form.resendTopicId || !defaultTopicId) return
    setForm((prev) => ({ ...prev, resendTopicId: defaultTopicId }))
  }, [creatingNew, defaultTopicId, form.resendTopicId])

  // Prefill test recipients per campaign: whoever got the last test, else the
  // signed-in admin.
  const selectedCampaignKey = selectedCampaign?.id ?? ''
  const lastTestRecipients = selectedCampaign?.lastTestRecipients.join(', ') ?? ''
  useEffect(() => {
    setTestRecipients(lastTestRecipients || adminEmail)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when the campaign changes
  }, [selectedCampaignKey, adminEmail])

  useEffect(() => {
    if (!syncRunning && !campaignLaunching) return

    const interval = window.setInterval(() => {
      refetchOverview()
      refetchCampaigns()
    }, 5000)
    return () => window.clearInterval(interval)
  }, [campaignLaunching, refetchCampaigns, refetchOverview, syncRunning])

  const invalidateEmailMarketing = () => {
    queryClient.invalidateQueries({ queryKey: overviewKey })
    queryClient.invalidateQueries({ queryKey: campaignListKey })
    queryClient.invalidateQueries({ queryKey: resourceListKey })
  }

  const handleSavedCampaign = (campaign: EmailMarketingCampaign | undefined, message: string) => {
    if (campaign) {
      setCreatingNew(false)
      setSelectedCampaignId(campaign.id)
      setForm(formFromCampaign(campaign))
    }
    invalidateEmailMarketing()
    toast.success(message)
  }

  const createMutation = useMutation(createEmailMarketingCampaign, {
    onSuccess: (response) => handleSavedCampaign(response.campaign, 'Draft created'),
    onError: (err: Error) => toast.error(`Save failed: ${err.message}`),
  })

  const updateMutation = useMutation(updateEmailMarketingCampaign, {
    onSuccess: (response) => handleSavedCampaign(response.campaign, 'Draft saved'),
    onError: (err: Error) => toast.error(`Save failed: ${err.message}`),
  })

  const testMutation = useMutation(sendEmailMarketingCampaignTest, {
    onSuccess: (response) => {
      if (response.campaign) {
        setSelectedCampaignId(response.campaign.id)
        setForm(formFromCampaign(response.campaign))
      }
      invalidateEmailMarketing()
      toast.success('Test email sent')
    },
    onError: (err: Error) => toast.error(`Test failed: ${err.message}`),
  })

  const launchMutation = useMutation(launchEmailMarketingCampaign, {
    onSuccess: (response) => {
      if (response.campaign) setSelectedCampaignId(response.campaign.id)
      setLaunchDialogOpen(false)
      invalidateEmailMarketing()
      toast.success('Campaign is sending')
    },
    onError: (err: Error) => toast.error(`Launch failed: ${err.message}`),
  })

  const refreshMutation = useMutation(refreshEmailMarketingCampaignStatus, {
    onSuccess: (response) => {
      if (response.campaign) setSelectedCampaignId(response.campaign.id)
      invalidateEmailMarketing()
      toast.success('Status refreshed')
    },
    onError: (err: Error) => toast.error(`Refresh failed: ${err.message}`),
  })

  const confirmDiscard = () =>
    !formDirty || window.confirm('You have unsaved changes. Discard them?')

  const handleNewCampaign = () => {
    if (!confirmDiscard()) return
    setCreatingNew(true)
    setSelectedCampaignId('')
    setForm(defaultForm(defaultTopicId))
  }

  const handleSelectCampaign = (campaign: EmailMarketingCampaign) => {
    if (campaign.id === selectedCampaign?.id) return
    if (!confirmDiscard()) return
    setCreatingNew(false)
    setSelectedCampaignId(campaign.id)
    setForm(formFromCampaign(campaign))
  }

  const handleDiscard = () => {
    if (selectedCampaign) {
      setForm(formFromCampaign(selectedCampaign))
      return
    }
    setForm(defaultForm(defaultTopicId))
  }

  const handleSave = (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault()
    if (selectedCampaignPending) {
      toast.error('Campaign is still loading. Try again in a moment.')
      return
    }
    const missing = missingFormFields(form)
    if (missing.length > 0) {
      toast.error(`Add a ${missing.join(', ')} before saving`)
      return
    }
    if (!formTopicExists) {
      toast.error('Pick a topic that exists in Resend before saving')
      return
    }

    const fields = {
      name: form.name,
      subject: form.subject,
      htmlBody: form.htmlBody,
      textBody: form.textBody,
      resendTopicId: form.resendTopicId,
      audience: form.audience,
    }
    if (creatingNew || !selectedCampaign) {
      createMutation.mutate(create(CreateEmailMarketingCampaignRequestSchema, fields))
      return
    }
    updateMutation.mutate(
      create(UpdateEmailMarketingCampaignRequestSchema, { id: selectedCampaign.id, ...fields }),
    )
  }

  const handleTestSend = () => {
    if (!selectedCampaign) return
    if (!postalAddressReady) {
      toast.error("Add Cortado's postal address in Settings before sending marketing email")
      return
    }
    const recipients = parseRecipients(testRecipients)
    if (recipients.length === 0) {
      toast.error('Add at least one test recipient')
      return
    }

    testMutation.mutate(
      create(SendEmailMarketingCampaignTestRequestSchema, {
        id: selectedCampaign.id,
        recipients,
      }),
    )
  }

  const handleLaunch = () => {
    if (!selectedCampaign) return
    if (!postalAddressReady) {
      toast.error("Add Cortado's postal address in Settings before sending marketing email")
      return
    }
    launchMutation.mutate(
      create(LaunchEmailMarketingCampaignRequestSchema, { id: selectedCampaign.id }),
    )
  }

  const handleRefreshCampaign = () => {
    if (!selectedCampaign) return
    refreshMutation.mutate(
      create(RefreshEmailMarketingCampaignStatusRequestSchema, { id: selectedCampaign.id }),
    )
  }

  if (
    overviewQuery.isLoading ||
    campaignsQuery.isLoading ||
    settingsQuery.isLoading ||
    (resendReady && (segmentsQuery.isLoading || topicsQuery.isLoading))
  ) {
    return (
      <PageHeader description="Loading…" />
    )
  }

  if (overviewQuery.isError || campaignsQuery.isError) {
    const error = overviewQuery.error ?? campaignsQuery.error
    return (
      <PageHeader
        description={error instanceof Error ? error.message : 'Unable to load email marketing'}
      />
    )
  }

  const saving = createMutation.isPending || updateMutation.isPending
  const missingFields = missingFormFields(form)
  const canSave =
    !selectedCampaignPending &&
    canEdit &&
    missingFields.length === 0 &&
    formTopicExists &&
    (creatingNew || !selectedCampaign || formDirty)
  const steps = launchChecklist({
    campaign: selectedCampaign,
    formDirty,
    topicValid: selectedCampaignTopicValid,
    deliveryReady,
    postalAddressReady,
  })
  const canLaunch = Boolean(
    selectedCampaign &&
      canEdit &&
      steps.every((step) => step.done) &&
      !launchMutation.isPending,
  )
  const canSendTest = Boolean(
    resendReady &&
      postalAddressReady &&
      selectedCampaign &&
      canEdit &&
      !formDirty &&
      testRecipients.trim() &&
      !testMutation.isPending,
  )
  const deliveryNeedsAttention = !deliveryReady || overview?.syncStatus === 'failed'
  const activeUserCount = overview?.activeUserCount ?? 0
  const waitlistCount = audienceMemberCount(overview, EmailMarketingAudienceEnum.WAITLIST)
  const recipientCount = audienceMemberCount(overview, selectedCampaign?.audience)
  const selectedAudienceLabel = audienceLabel(selectedCampaign?.audience)

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        description={`Write, test, and send broadcasts. ${activeUserCount.toLocaleString()} users with email · ${waitlistCount.toLocaleString()} on the waitlist.`}
        actions={
          <>
            <Button variant="outline" onClick={() => setDeliveryOpen(true)}>
              {deliveryNeedsAttention ? (
                <AlertTriangle className="h-4 w-4 text-status-warning-fg" />
              ) : (
                <Users className="h-4 w-4" />
              )}
              Audience & delivery
            </Button>
            <Button onClick={handleNewCampaign}>
              <Plus className="h-4 w-4" />
              New campaign
            </Button>
          </>
        }
      />

      {deliveryNeedsAttention ? (
        <AttentionBanner
          message={
            !resendReady
              ? 'Resend is not configured. You can write drafts, but nothing can be tested or sent.'
              : !allResourcesReady
                ? 'Delivery setup is incomplete, so campaigns cannot launch.'
                : 'The last contact sync failed. New users may be missing from the audience.'
          }
          action={
            resendReady ? (
              <Button size="sm" variant="outline" onClick={() => setDeliveryOpen(true)}>
                Review setup
              </Button>
            ) : null
          }
        />
      ) : null}

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
        <CampaignList
          campaigns={campaigns}
          topics={topics}
          creatingNew={creatingNew}
          draftName={form.name}
          selectedCampaign={selectedCampaign}
          onSelect={handleSelectCampaign}
          onNew={handleNewCampaign}
        />

        <div className="flex min-w-0 flex-col gap-4">
          <WorkspaceHeader
            title={form.name.trim() || selectedCampaign?.name || 'Untitled campaign'}
            campaign={selectedCampaign}
            meta={`${selectedAudienceLabel} · ${selectedCampaignTopic.label}`}
            actions={
              canEdit ? (
                <>
                  {formDirty ? (
                    <Button type="button" variant="ghost" onClick={handleDiscard} disabled={saving}>
                      <Undo2 className="h-4 w-4" />
                      Discard
                    </Button>
                  ) : null}
                  <Button type="submit" form="campaign-form" disabled={!canSave || saving}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {selectedCampaign ? 'Save draft' : 'Create draft'}
                  </Button>
                </>
              ) : null
            }
            saveHint={
              canEdit
                ? missingFields.length > 0
                  ? `Still needed: ${missingFields.join(', ')}`
                  : formDirty
                    ? 'Unsaved changes'
                    : selectedCampaign
                      ? 'All changes saved'
                      : ''
                : ''
            }
          />

          {!canEdit && selectedCampaign ? (
            <Notice icon={<Lock className="mt-0.5 h-4 w-4 shrink-0" />}>
              {selectedCampaign.status === EmailMarketingCampaignStatusEnum.SENT
                ? 'This campaign has been sent. Its content is locked.'
                : 'This campaign is sending. Its content is locked.'}
            </Notice>
          ) : null}
          {selectedCampaign?.errorMessage ? (
            <Notice tone="destructive">
              Last launch failed: {selectedCampaign.errorMessage}. Fix the issue and launch again.
            </Notice>
          ) : null}
          {selectedCampaign && canEdit && selectedCampaignTopic.missing ? (
            <Notice tone="destructive">
              The saved topic no longer exists in Resend. Pick another topic and save.
            </Notice>
          ) : null}

          <div className="grid grid-cols-1 items-start gap-4 2xl:grid-cols-[minmax(0,1fr)_340px]">
            <CampaignEditor
              form={form}
              setForm={setForm}
              topics={topics}
              audienceCounts={(audience) => audienceMemberCount(overview, audience)}
              missingTopicLabel={
                selectedCampaignTopic.missing && form.resendTopicId ? selectedCampaignTopic.label : ''
              }
              postalAddress={postalAddress}
              disabled={!canEdit || saving}
              onSubmit={handleSave}
            />

            {canEdit ? (
              <LaunchPanel
                steps={steps}
                campaignSaved={Boolean(selectedCampaign)}
                testRecipients={testRecipients}
                setTestRecipients={setTestRecipients}
                lastTestSentAt={
                  selectedCampaign?.hasSuccessfulCurrentTest && !formDirty
                    ? formatTimestamp(selectedCampaign.lastTestSentAt)
                    : ''
                }
                canSendTest={canSendTest}
                testPending={testMutation.isPending}
                onTestSend={handleTestSend}
                canLaunch={canLaunch}
                audienceSummary={
                  selectedCampaign && isAudienceSelected(selectedCampaign.audience)
                    ? `${recipientCount.toLocaleString()} in ${selectedAudienceLabel}, minus anyone unsubscribed from ${selectedCampaignTopic.label}`
                    : 'Pick an audience to see who receives this.'
                }
                onOpenDelivery={() => setDeliveryOpen(true)}
                onOpenLaunch={() => setLaunchDialogOpen(true)}
              />
            ) : selectedCampaign ? (
              <SendStatusPanel
                campaign={selectedCampaign}
                audienceLabel={selectedAudienceLabel}
                topicLabel={selectedCampaignTopic.label}
                canRefresh={Boolean(
                  resendReady && selectedCampaign.resendBroadcastId && !refreshMutation.isPending,
                )}
                refreshPending={refreshMutation.isPending}
                onRefresh={handleRefreshCampaign}
              />
            ) : null}
          </div>
        </div>
      </div>

      <LaunchDialog
        open={launchDialogOpen}
        onOpenChange={setLaunchDialogOpen}
        campaign={selectedCampaign}
        topicLabel={selectedCampaignTopic.label}
        audienceName={selectedAudienceLabel}
        recipientCount={recipientCount}
        canLaunch={canLaunch}
        launchPending={launchMutation.isPending}
        onLaunch={handleLaunch}
      />

      <DeliverySheet
        open={deliveryOpen}
        onOpenChange={setDeliveryOpen}
        overview={overview}
        deliveryReady={deliveryReady}
        segments={{
          resources: segments,
          loading: segmentsQuery.isLoading,
          error: segmentsQuery.error,
        }}
        topics={{ resources: topics, loading: topicsQuery.isLoading, error: topicsQuery.error }}
        onChanged={invalidateEmailMarketing}
        onTopicCreated={(topicId) => {
          if (canEdit && !form.resendTopicId) {
            setForm((prev) => ({ ...prev, resendTopicId: topicId }))
          }
        }}
      />
    </div>
  )
}

function PageHeader({ description, actions }: { description: string; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold">Email Marketing</h1>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  )
}

function AttentionBanner({ message, action }: { message: string; action: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-status-warning-border bg-status-warning-bg px-4 py-3 text-sm text-status-warning-fg">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{message}</span>
      </div>
      {action}
    </div>
  )
}

function Notice({
  tone = 'muted',
  icon,
  children,
}: {
  tone?: 'muted' | 'destructive'
  icon?: ReactNode
  children: ReactNode
}) {
  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-md border p-3 text-sm',
        tone === 'destructive'
          ? 'border-destructive/30 bg-destructive/5 text-destructive'
          : 'bg-muted/50 text-muted-foreground',
      )}
    >
      {icon ?? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
      <span>{children}</span>
    </div>
  )
}

function WorkspaceHeader({
  title,
  campaign,
  meta,
  actions,
  saveHint,
}: {
  title: string
  campaign?: EmailMarketingCampaign
  meta: string
  actions: ReactNode
  saveHint: string
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h2 className="truncate text-lg font-semibold">{title}</h2>
          <Badge variant={statusVariant(campaign?.status)}>
            {campaign ? statusLabel(campaign.status) : 'New'}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          {campaign
            ? `${meta} · Updated ${formatTimestamp(campaign.updatedAt)}`
            : 'Not saved yet'}
        </p>
      </div>
      {actions ? (
        <div className="flex items-center gap-2">
          {saveHint ? <span className="text-xs text-muted-foreground">{saveHint}</span> : null}
          {actions}
        </div>
      ) : null}
    </div>
  )
}

function CampaignEditor({
  form,
  setForm,
  topics,
  audienceCounts,
  missingTopicLabel,
  postalAddress,
  disabled,
  onSubmit,
}: {
  form: CampaignForm
  setForm: (update: (prev: CampaignForm) => CampaignForm) => void
  topics: EmailMarketingResource[]
  audienceCounts: (audience: EmailMarketingAudienceEnum) => number
  missingTopicLabel: string
  postalAddress: string
  disabled: boolean
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
}) {
  const [view, setView] = useState<'write' | 'preview'>('write')
  const update = <K extends keyof CampaignForm>(key: K, value: CampaignForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))
  const generatedText = htmlToPlainText(form.htmlBody)

  return (
    <Card>
      <CardContent>
        <form id="campaign-form" onSubmit={onSubmit} className="grid gap-5">
          <Field
            label="Campaign name"
            htmlFor="campaign-name"
            hint="Only visible to Cortado admins."
          >
            <Input
              id="campaign-name"
              value={form.name}
              disabled={disabled}
              placeholder="e.g. October product update"
              onChange={(event) => update('name', event.target.value)}
            />
          </Field>

          <div className="grid gap-4 md:grid-cols-2">
            <Field
              label="Audience"
              htmlFor="campaign-audience"
              hint={audienceDescription(form.audience) || 'Who receives this campaign.'}
            >
              <Select
                value={isAudienceSelected(form.audience) ? String(form.audience) : ''}
                disabled={disabled}
                onValueChange={(value) =>
                  update('audience', Number(value) as EmailMarketingAudienceEnum)
                }
              >
                <SelectTrigger id="campaign-audience" className="w-full">
                  <SelectValue placeholder="Pick an audience" />
                </SelectTrigger>
                <SelectContent>
                  {EMAIL_MARKETING_AUDIENCES.map((audience) => (
                    <SelectItem key={audience} value={String(audience)}>
                      {audienceLabel(audience)} · {audienceCounts(audience).toLocaleString()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field
              label="Topic"
              htmlFor="campaign-topic"
              hint="Users unsubscribed from it are skipped."
            >
              <Select
                value={form.resendTopicId}
                disabled={disabled}
                onValueChange={(value) => update('resendTopicId', value)}
              >
                <SelectTrigger id="campaign-topic" className="w-full">
                  <SelectValue placeholder="Pick a topic" />
                </SelectTrigger>
                <SelectContent>
                  {missingTopicLabel ? (
                    <SelectItem value={form.resendTopicId} disabled>
                      {missingTopicLabel}
                    </SelectItem>
                  ) : null}
                  {topics.map((topic) => (
                    <SelectItem key={topic.id} value={topic.id}>
                      {topic.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Subject line" htmlFor="campaign-subject">
            <Input
              id="campaign-subject"
              value={form.subject}
              disabled={disabled}
              placeholder="What recipients see in their inbox"
              onChange={(event) => update('subject', event.target.value)}
            />
          </Field>

          <div className="grid gap-2">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="campaign-html">Email body</Label>
              <div className="inline-flex rounded-md border p-0.5" role="group" aria-label="Body view">
                {(['write', 'preview'] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={view === option}
                    onClick={() => setView(option)}
                    className={cn(
                      'rounded-sm px-2.5 py-1 text-xs font-medium capitalize text-muted-foreground transition-colors',
                      view === option && 'bg-muted text-foreground',
                    )}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
            {view === 'write' ? (
              <CampaignHtmlEditor
                id="campaign-html"
                value={form.htmlBody}
                disabled={disabled}
                onChange={(htmlBody) => update('htmlBody', htmlBody)}
              />
            ) : (
              <EmailPreview subject={form.subject} html={form.htmlBody} postalAddress={postalAddress} />
            )}
            <p className="text-xs text-muted-foreground">
              An unsubscribe link and Cortado&apos;s postal address are added to the bottom of every
              email. To place the link yourself, link to <code>{UNSUBSCRIBE_URL_PLACEHOLDER}</code>.
              {postalAddress
                ? null
                : ' Add the address in Settings before this email can be sent.'}
            </p>
          </div>

          <Field
            label="Plain-text version"
            htmlFor="campaign-text"
            hint="Shown by email clients that can't display HTML. The unsubscribe line and postal address are added when it sends."
            action={
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={disabled || !generatedText}
                onClick={() => {
                  if (
                    form.textBody.trim() &&
                    !window.confirm('Replace the current plain-text version?')
                  ) {
                    return
                  }
                  update('textBody', generatedText)
                }}
              >
                <Sparkles className="h-4 w-4" />
                Generate from email body
              </Button>
            }
          >
            <Textarea
              id="campaign-text"
              value={form.textBody}
              disabled={disabled}
              onChange={(event) => update('textBody', event.target.value)}
              className="min-h-40"
            />
          </Field>
        </form>
      </CardContent>
    </Card>
  )
}

function Field({
  label,
  htmlFor,
  hint,
  action,
  children,
}: {
  label: string
  htmlFor: string
  hint?: string
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="grid gap-2">
      <div className="flex min-h-8 items-center justify-between gap-3">
        <Label htmlFor={htmlFor}>{label}</Label>
        {action}
      </div>
      {children}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  )
}

function EmailPreview({
  subject,
  html,
  postalAddress,
}: {
  subject: string
  html: string
  postalAddress: string
}) {
  return (
    <div className="overflow-hidden rounded-md border">
      <div className="flex items-center gap-2 border-b bg-muted/50 px-3 py-2 text-sm">
        <Mail className="h-4 w-4 text-muted-foreground" />
        <span className={cn('truncate font-medium', !subject && 'text-muted-foreground')}>
          {subject || 'No subject'}
        </span>
      </div>
      <iframe
        title="Email preview"
        sandbox=""
        srcDoc={
          html
            ? withUnsubscribeFooterPreview(html, postalAddress)
            : '<div style="font-family: sans-serif; padding: 16px; color: #64748b;">Nothing to preview yet.</div>'
        }
        className="h-[480px] w-full bg-white"
      />
      {postalAddress ? null : (
        <p className="border-t px-3 py-2 text-xs text-destructive">
          Add Cortado&apos;s postal address in{' '}
          <Link to="/settings" className="underline underline-offset-2">
            Settings
          </Link>{' '}
          before this email can be sent.
        </p>
      )}
    </div>
  )
}

function CampaignList({
  campaigns,
  topics,
  creatingNew,
  draftName,
  selectedCampaign,
  onSelect,
  onNew,
}: {
  campaigns: EmailMarketingCampaign[]
  topics: EmailMarketingResource[]
  creatingNew: boolean
  draftName: string
  selectedCampaign?: EmailMarketingCampaign
  onSelect: (campaign: EmailMarketingCampaign) => void
  onNew: () => void
}) {
  const showNewItem = creatingNew || campaigns.length === 0

  return (
    <nav aria-label="Campaigns" className="flex flex-col gap-2 lg:sticky lg:top-4">
      <p className="px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Campaigns · {campaigns.length}
      </p>
      {showNewItem ? (
        <div className="rounded-md border border-primary bg-muted p-3 text-sm" aria-current="true">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate font-medium">{draftName.trim() || 'Untitled campaign'}</span>
            <Badge variant="secondary">New</Badge>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">Not saved yet</p>
        </div>
      ) : null}
      {campaigns.map((campaign) => {
        const selected = !creatingNew && selectedCampaign?.id === campaign.id
        const topicStatus = campaignTopicStatus(campaign, topics)
        return (
          <button
            key={campaign.id}
            type="button"
            aria-current={selected ? 'true' : undefined}
            onClick={() => onSelect(campaign)}
            className={cn(
              'rounded-md border p-3 text-left text-sm transition-colors hover:bg-muted/70',
              selected ? 'border-primary bg-muted' : 'bg-background',
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <p className="truncate font-medium">{campaign.name}</p>
              <Badge variant={statusVariant(campaign.status)}>{statusLabel(campaign.status)}</Badge>
            </div>
            <p className="mt-1 truncate text-xs text-muted-foreground">
              {campaign.subject || 'No subject'}
            </p>
            <div className="mt-2 flex items-center justify-between gap-2 text-xs text-muted-foreground">
              <span className={cn('truncate', topicStatus.missing && 'text-destructive')}>
                {audienceLabel(campaign.audience)} · {topicStatus.label}
              </span>
              <span className="shrink-0">{formatTimestamp(lastCampaignActivity(campaign))}</span>
            </div>
          </button>
        )
      })}
      {!showNewItem ? (
        <Button type="button" variant="ghost" size="sm" className="justify-start" onClick={onNew}>
          <Plus className="h-4 w-4" />
          New campaign
        </Button>
      ) : null}
    </nav>
  )
}

function LaunchPanel({
  steps,
  campaignSaved,
  testRecipients,
  setTestRecipients,
  lastTestSentAt,
  canSendTest,
  testPending,
  onTestSend,
  canLaunch,
  audienceSummary,
  onOpenDelivery,
  onOpenLaunch,
}: {
  steps: LaunchStep[]
  campaignSaved: boolean
  testRecipients: string
  setTestRecipients: (value: string) => void
  lastTestSentAt: string
  canSendTest: boolean
  testPending: boolean
  onTestSend: () => void
  canLaunch: boolean
  audienceSummary: string
  onOpenDelivery: () => void
  onOpenLaunch: () => void
}) {
  const remaining = steps.filter((step) => !step.done).length

  return (
    <Card className="2xl:sticky 2xl:top-4">
      <CardHeader>
        <CardTitle>Ready to send</CardTitle>
        <CardDescription>
          {remaining === 0
            ? 'Everything checks out.'
            : `${remaining} step${remaining === 1 ? '' : 's'} left before you can launch.`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-5">
          <ol className="flex flex-col gap-3">
            {steps.map((step) => (
              <li key={step.id} className="flex items-start gap-2.5 text-sm">
                {step.done ? (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-label="Done" />
                ) : (
                  <Circle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-label="To do" />
                )}
                <div className="min-w-0">
                  <p className={cn(step.done ? 'text-muted-foreground' : 'font-medium')}>{step.label}</p>
                  {!step.done ? (
                    step.id === 'delivery' ? (
                      <button
                        type="button"
                        onClick={onOpenDelivery}
                        className="text-xs text-primary underline-offset-2 hover:underline"
                      >
                        {step.hint}
                      </button>
                    ) : step.id === 'postal' ? (
                      <Link
                        to="/settings"
                        className="text-xs text-primary underline-offset-2 hover:underline"
                      >
                        {step.hint}
                      </Link>
                    ) : (
                      <p className="text-xs text-muted-foreground">{step.hint}</p>
                    )
                  ) : step.id === 'tested' && lastTestSentAt ? (
                    <p className="text-xs text-muted-foreground">Sent {lastTestSentAt}</p>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
  
          <div className="grid gap-2 border-t pt-4">
            <Label htmlFor="test-recipients">Send a test to</Label>
            <div className="flex gap-2">
              <Input
                id="test-recipients"
                value={testRecipients}
                disabled={!campaignSaved}
                placeholder="you@cortado.pro"
                onChange={(event) => setTestRecipients(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && canSendTest) {
                    event.preventDefault()
                    onTestSend()
                  }
                }}
              />
              <Button type="button" variant="outline" onClick={onTestSend} disabled={!canSendTest}>
                {testPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Separate multiple addresses with commas.</p>
          </div>
  
          <div className="grid gap-2 border-t pt-4">
            <Button type="button" onClick={onOpenLaunch} disabled={!canLaunch}>
              <Rocket className="h-4 w-4" />
              Launch campaign
            </Button>
            <p className="text-center text-xs text-muted-foreground">{audienceSummary}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function SendStatusPanel({
  campaign,
  audienceLabel,
  topicLabel,
  canRefresh,
  refreshPending,
  onRefresh,
}: {
  campaign: EmailMarketingCampaign
  audienceLabel: string
  topicLabel: string
  canRefresh: boolean
  refreshPending: boolean
  onRefresh: () => void
}) {
  return (
    <Card className="2xl:sticky 2xl:top-4">
      <CardHeader>
        <CardTitle>Delivery</CardTitle>
        <CardDescription>
          {campaign.status === EmailMarketingCampaignStatusEnum.SENT
            ? 'Resend has accepted this broadcast.'
            : 'Resend is sending this broadcast. Status updates every few seconds.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-4">
          <dl className="grid gap-2 text-sm">
            <SummaryRow label="Status" value={statusLabel(campaign.status)} />
            <SummaryRow label="Audience" value={audienceLabel} />
            <SummaryRow label="Topic" value={topicLabel} />
            <SummaryRow label="Launched" value={formatTimestamp(campaign.launchedAt)} />
            <SummaryRow label="Sent" value={formatTimestamp(campaign.sentAt)} />
            <SummaryRow label="Last test" value={formatTimestamp(campaign.lastTestSentAt)} />
          </dl>
          {campaign.resendBroadcastId ? (
            <p className="break-all font-mono text-xs text-muted-foreground">
              Broadcast {campaign.resendBroadcastId}
            </p>
          ) : null}
          <Button type="button" variant="outline" onClick={onRefresh} disabled={!canRefresh}>
            {refreshPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh status
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="truncate font-medium">{value}</dd>
    </div>
  )
}

function LaunchDialog({
  open,
  onOpenChange,
  campaign,
  topicLabel,
  audienceName,
  recipientCount,
  canLaunch,
  launchPending,
  onLaunch,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  campaign?: EmailMarketingCampaign
  topicLabel: string
  audienceName: string
  recipientCount: number
  canLaunch: boolean
  launchPending: boolean
  onLaunch: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Launch “{campaign?.name ?? 'campaign'}”?</DialogTitle>
          <DialogDescription>
            This sends the email now. It cannot be edited or recalled afterwards.
          </DialogDescription>
        </DialogHeader>
        <dl className="grid gap-2 rounded-md border p-3 text-sm">
          <SummaryRow label="Subject" value={campaign?.subject || '-'} />
          <SummaryRow label="Audience" value={`${audienceName} · ${recipientCount.toLocaleString()}`} />
          <SummaryRow label="Topic" value={topicLabel} />
          <SummaryRow label="Last test" value={formatTimestamp(campaign?.lastTestSentAt, 'None')} />
        </dl>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={onLaunch} disabled={!canLaunch}>
            {launchPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
            Send to {audienceName.toLowerCase()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
